import { useEffect } from "react";
import { useSelector, shallowEqual } from "react-redux";
import { useTranslation } from "react-i18next";

import { FormControl, FormLabel } from "@mui/material";

import { RootState } from "@app/store";
import { getSelectedCustomerId } from "@features/customers/customerSlice";
import { formControlStyle, formLabelStyle } from "@features/common/styles";
import { useAppDispatch } from "@features/common/StoreHook";
import SortTable, { IProperty } from "@features/common/Table/SortTable";
import { selectSecretsStatus } from "./secretsSlice";
import { SecretResource } from "./types";
import CloudPlatformChip from "@features/common/CloudPlatformChip";
import {
  getSecretResources,
  fetchSecretResources,
  getSelectedSecretResource,
  selectSecretResource,
} from "./secretResourcesSlice";

interface Resource extends Omit<SecretResource, "cloud_platform"> {
  cloudPlatform?: JSX.Element | null;
}

export const SecretResources = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const customerId = useSelector(getSelectedCustomerId);
  const resources: any[] = useSelector(
    (state: RootState) => getSecretResources(state),
    shallowEqual,
  );
  const isLoading = useSelector(selectSecretsStatus) === "loading";
  const selectedSecretResource: string | undefined = useSelector(
    getSelectedSecretResource,
  );

  const handleRadioButtonChange = (id: string) => {
    dispatch(selectSecretResource(id));
  };

  const secretProperties: IProperty<Resource>[] = [
    { key: "resource_name", label: t("label-resource") },
    { key: "cloudPlatform", label: t("label-cloud-platform") },
  ];

  useEffect(() => {
    dispatch(fetchSecretResources(customerId));
  }, []);

  useEffect(() => {
    // If there are resources, select the first one by default to display its secrets
    if (!selectedSecretResource && resources.length > 0) {
      dispatch(selectSecretResource(resources[0].resource_name));
    }
  }, [resources]);

  const secretResources = resources.map((resource) => ({
    ...resource,
    cloudPlatform: (
      <CloudPlatformChip cloudPlatform={resource.cloud_platform} />
    ),
  }));

  return (
    <>
      <FormControl component="fieldset" sx={formControlStyle}>
        <FormLabel component="legend" sx={formLabelStyle}>
          {t("label-secret-resources")}
        </FormLabel>
        <SortTable
          properties={secretProperties}
          idProperty={"resource_name"}
          data={secretResources}
          isLoading={isLoading}
          selectedId={selectedSecretResource}
          handleChange={handleRadioButtonChange}
          pagination={true}
        />
      </FormControl>
    </>
  );
};
